import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import authService from '../../api-authorization/AuthorizeService';
import styles from './Reports.module.css';

// ─── helpers ────────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { label: 'Current week',      value: 'current-week' },
  { label: 'Current month',     value: 'current-month' },
  { label: 'Last month',        value: 'last-month' },
  { label: 'Last quarter',      value: 'last-quarter' },
  { label: 'Year to date',      value: 'year-to-date' },
  { label: 'Custom date range', value: 'custom' },
];

const GROUP_BY_OPTIONS = [
  { value: 'Field',     label: 'Block' },
  { value: 'Farm',      label: 'Farm' },
  { value: 'FarmEvent', label: 'Farm Event' },
  { value: 'Sprayer',   label: 'Sprayer' },
];

const TABLE_COLS = [
  { key: 'date',              label: 'Date' },
  { key: 'farmName',          label: 'Farm Name' },
  { key: 'blockName',         label: 'Block Name' },
  { key: 'farmEvent',         label: 'Farm Event' },
  { key: 'sprayerName',       label: 'Sprayer Name' },
  { key: 'productAppliedGal', label: 'Product Applied' },
  { key: 'productSavedPct',   label: 'Product Saved (%)' },
  { key: 'totalAreaAcres',    label: 'Total Area (ac)' },
  { key: 'appliedAreaAcres',  label: 'Applied Area (ac)' },
  { key: 'targetRateGalAcre', label: 'Target Rate (gal/ac)' },
  { key: 'actualRateGalAcre', label: 'Actual Rate (gal/ac)' },
];

function periodToDates(period) {
  const now = dayjs();
  switch (period) {
    case 'current-week':  return { dateFrom: now.startOf('week').add(1, 'day'), dateTo: now };
    case 'current-month': return { dateFrom: now.startOf('month'), dateTo: now };
    case 'last-month':    return { dateFrom: now.subtract(1, 'month').startOf('month'), dateTo: now.subtract(1, 'month').endOf('month') };
    case 'last-quarter': {
      const currentQ = Math.floor(now.month() / 3);           // 0-3
      const lastQStartMonth = currentQ === 0
        ? now.subtract(1, 'year').month(9)                    // Q4 of previous year starts Oct
        : now.month((currentQ - 1) * 3);
      const lastQStart = lastQStartMonth.startOf('month');
      const lastQEnd   = lastQStart.add(2, 'month').endOf('month');
      return { dateFrom: lastQStart, dateTo: lastQEnd };
    }
    case 'year-to-date':  return { dateFrom: now.startOf('year'), dateTo: now };
    default:              return { dateFrom: null, dateTo: null };
  }
}

// ─── sub-components ─────────────────────────────────────────────────────────

function StatCard({ label, value, unit }) {
  return (
    <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight="bold">
        {value != null ? Number(value).toLocaleString() : '-'}
        {unit && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>{unit}</Typography>}
      </Typography>
    </Paper>
  );
}

function SummaryBar({ summary }) {
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatCard label="Total Product Saved" value={summary.totalProductSavedGal} unit="gal" />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatCard label="Total Area" value={summary.totalAreaAcres} unit="ac" />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatCard label="Total Applied Area" value={summary.totalAppliedAreaAcres} unit="ac" />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatCard label="Block Count" value={summary.blockCount} />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatCard label="Farm Event Count" value={summary.farmEventCount} />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <StatCard label="Sprayer Count" value={summary.sprayerCount} />
      </Grid>
    </Grid>
  );
}

function ReportTable({ rows, totals, isBroadcast }) {
  const broadcastTotalAccuracy = useMemo(() => {
    if (!isBroadcast || !rows?.length) return null;
    const theoretical = rows.reduce((sum, r) => {
      const area = Number(r.appliedAreaAcres);
      const rate = Number(r.targetRateGalAcre);
      return sum + (isFinite(area) && isFinite(rate) ? area * rate : 0);
    }, 0);
    if (theoretical === 0) return null;
    const actual = rows.reduce((sum, r) => {
      const v = Number(r.productAppliedGal);
      return sum + (isFinite(v) ? v : 0);
    }, 0);
    return (actual / theoretical) * 100;
  }, [isBroadcast, rows]);

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            {TABLE_COLS.map(c => (
              <TableCell key={c.key} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {isBroadcast && c.key === 'productSavedPct' ? 'Target Accuracy (%)' : c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? 'white' : 'grey.50' }}>
              {TABLE_COLS.map(c => {
                if (isBroadcast && c.key === 'productSavedPct') {
                  const target = Number(row.targetRateGalAcre);
                  const actual = Number(row.actualRateGalAcre);
                  const accuracy = target > 0 ? (actual / target) * 100 : null;
                  return (
                    <TableCell key={c.key} sx={{ whiteSpace: 'nowrap' }}>
                      {accuracy !== null ? `${accuracy.toFixed(1)}%` : '-'}
                    </TableCell>
                  );
                }
                return <TableCell key={c.key} sx={{ whiteSpace: 'nowrap' }}>{row[c.key]}</TableCell>;
              })}
            </TableRow>
          ))}
          {totals && (
            <TableRow sx={{ backgroundColor: 'grey.100', fontWeight: 700 }}>
              <TableCell colSpan={5} sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{totals.totalProductAppliedGal}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {isBroadcast
                  ? (broadcastTotalAccuracy !== null ? `${broadcastTotalAccuracy.toFixed(1)}%` : '-')
                  : `${totals.totalProductSavedPct}%`}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{totals.totalAreaAcres}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{totals.totalAppliedAreaAcres}</TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── scrollable checkbox list ────────────────────────────────────────────────

// ─── multi-select dropdown ──────────────────────────────────────────────────

function MultiSelect({ label, items, selectedValues, onToggle, onSelectAll, onDeselectAll, emptyText, minWidth = 160 }) {
  const allSelected = items.length > 0 && selectedValues.length === items.length;
  const someSelected = selectedValues.length > 0 && !allSelected;
  const displayValue = selectedValues.length === 0
    ? 'None'
    : allSelected
      ? 'All'
      : selectedValues.length === 1
        ? items.find(i => i.value === selectedValues[0])?.label ?? '1 selected'
        : `${selectedValues.length} selected`;

  return (
    <FormControl sx={{ minWidth: { xs: '100%', sm: minWidth } }}>
      <FormLabel sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>{label}</FormLabel>
      <Select
        size="small"
        multiple
        displayEmpty
        value={selectedValues}
        renderValue={() =>
          items.length === 0
            ? <em style={{ color: '#999' }}>{emptyText}</em>
            : displayValue
        }
      >
        {(onSelectAll || onDeselectAll) && (
          <MenuItem value="__all__" onClick={allSelected ? onDeselectAll : onSelectAll}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              color="primary"
            />
            <ListItemText primary={allSelected ? 'Deselect All' : 'Select All'} />
          </MenuItem>
        )}
        {items.map(({ value, label: itemLabel }) => (
          <MenuItem key={value} value={value} onClick={() => onToggle(value)} dense>
            <Checkbox size="small" checked={selectedValues.includes(value)} color="primary" />
            <ListItemText primary={itemLabel} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const [period, setPeriod]                     = useState('current-week');
  const [customFrom, setCustomFrom]             = useState(null);
  const [customTo, setCustomTo]                 = useState(null);
  const [groupBy, setGroupBy]                   = useState('Field');
  const [allSubLocations, setAllSubLocations]   = useState([]);
  const [selectedFarmNames, setSelectedFarmNames] = useState([]);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  const [selectedSprayModes, setSelectedSprayModes] = useState(['Precision', 'Broadcast']);
  const SPRAY_MODES = ['Precision', 'Broadcast'];
  const [reportData, setReportData]             = useState(null);
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState(null);

  // ── fetch all sublocations once ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await authService.getAccessToken();
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/sublocations/all`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setAllSubLocations(data);
          const names = [...new Set(data.map(sl => sl.farmName).filter(Boolean))];
          setSelectedFarmNames(names);
          setSelectedFieldIds(data.map(sl => sl.id));
        }
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const allFarmNames = useMemo(
    () => [...new Set(allSubLocations.map(sl => sl.farmName).filter(Boolean))].sort(),
    [allSubLocations]
  );

  const visibleFields = useMemo(
    () => selectedFarmNames.length > 0
      ? allSubLocations.filter(sl => selectedFarmNames.includes(sl.farmName))
      : allSubLocations,
    [allSubLocations, selectedFarmNames]
  );

  const prevVisibleIdsRef = React.useRef(null);
  useEffect(() => {
    const visibleIds = new Set(visibleFields.map(f => f.id));
    const prev = prevVisibleIdsRef.current;
    setSelectedFieldIds(cur => {
      const kept  = cur.filter(id => visibleIds.has(id));
      const added = prev === null ? [] : visibleFields.filter(f => !prev.has(f.id)).map(f => f.id);
      return [...new Set([...kept, ...added])];
    });
    prevVisibleIdsRef.current = visibleIds;
  }, [visibleFields]);


  // ── run report ──
  const runReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await authService.getAccessToken();
      const { dateFrom, dateTo } = period === 'custom'
        ? { dateFrom: customFrom, dateTo: customTo }
        : periodToDates(period);

      const payload = {
        dateFrom: dateFrom ? dateFrom.format('YYYY-MM-DD') : null,
        dateTo:   dateTo   ? dateTo.format('YYYY-MM-DD')   : null,
        groupBy,
        subLocationIds: selectedFieldIds,
        sprayModes:     selectedSprayModes,
      };

      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/api/reports/spray-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setReportData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [period, customFrom, customTo, groupBy, selectedFieldIds, selectedSprayModes]);

  useEffect(() => {
    if (period === 'custom' && (!customFrom || !customTo)) return;
    if (allSubLocations.length === 0) return;
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customFrom, customTo, groupBy, selectedFieldIds, selectedSprayModes, allSubLocations.length]);

  // ── exports ──
  const exportCsv = () => {
    if (!reportData) return;
    const header = TABLE_COLS.map(c => c.label).join(',');
    const csvRows = [header];
    reportData.groups?.forEach(g =>
      g.rows?.forEach(r => csvRows.push(TABLE_COLS.map(c => r[c.key] ?? '').join(',')))
    );
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'spray-report.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── toggle helpers ──
  const toggleFarm = (name) =>
    setSelectedFarmNames(p => p.includes(name) ? p.filter(x => x !== name) : [...p, name]);
  const toggleField = (id) =>
    setSelectedFieldIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleSprayMode = (mode) =>
    setSelectedSprayModes(p => p.includes(mode) ? p.filter(x => x !== mode) : [...p, mode]);

  const hasResults = reportData && !loading && reportData.groups?.length > 0;

  // ── render ──
  return (
    <Box className={styles.container}>
      <Typography variant="h4" fontWeight="bold" textAlign="center" mb={3}>Reports</Typography>

      {/* ── Filters ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={3}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        mb={3}
        justifyContent="center"
      >

        {/* Period */}
        <FormControl sx={{ minWidth: { xs: '100%', sm: 180 } }}>
          <FormLabel sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Period</FormLabel>
          <Select
            size="small"
            value={period}
            onChange={e => setPeriod(e.target.value)}
          >
            {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
          {period !== 'custom' && (() => {
            const { dateFrom, dateTo } = periodToDates(period);
            return dateFrom && dateTo
              ? <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                  {dateFrom.format('MMM D, YY')} – {dateTo.format('MMM D, YY')}
                </Typography>
              : null;
          })()}
          {period === 'custom' && (
            <Stack direction="row" gap={1} mt={1} alignItems="flex-start">
              <Box>
                <FormLabel sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, display: 'block' }}>From</FormLabel>
                <DatePicker
                  value={customFrom}
                  onChange={setCustomFrom}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Box>
              <Box>
                <FormLabel sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, display: 'block' }}>To</FormLabel>
                <DatePicker
                  value={customTo}
                  onChange={setCustomTo}
                  slotProps={{ textField: { size: 'small' } }}
                />
              </Box>
            </Stack>
          )}
        </FormControl>

        {/* Farm */}
        <MultiSelect
          label="Farm"
          items={allFarmNames.map(n => ({ value: n, label: n }))}
          selectedValues={selectedFarmNames}
          onToggle={toggleFarm}
          onSelectAll={() => setSelectedFarmNames(allFarmNames)}
          onDeselectAll={() => setSelectedFarmNames([])}
          emptyText="Loading…"
          minWidth={180}
        />

        {/* Block */}
        <MultiSelect
          label="Block"
          items={[...visibleFields]
            .sort((a, b) => a.farmName.localeCompare(b.farmName) || a.name.localeCompare(b.name))
            .map(f => ({ value: f.id, label: `${f.farmName} - ${f.name}` }))}
          selectedValues={selectedFieldIds}
          onToggle={toggleField}
          onSelectAll={() => setSelectedFieldIds(visibleFields.map(f => f.id))}
          onDeselectAll={() => setSelectedFieldIds([])}
          emptyText="Select a farm"
          minWidth={180}
        />

        {/* Group By */}
        <FormControl sx={{ minWidth: { xs: '100%', sm: 140 } }}>
          <FormLabel sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Group By</FormLabel>
          <Select
            size="small"
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
          >
            {GROUP_BY_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Spray Mode */}
        <MultiSelect
          label="Spray Mode"
          items={SPRAY_MODES.map(m => ({ value: m, label: m }))}
          selectedValues={selectedSprayModes}
          onToggle={toggleSprayMode}
          onSelectAll={() => setSelectedSprayModes([...SPRAY_MODES])}
          onDeselectAll={() => setSelectedSprayModes([])}
          emptyText=""
        />
      </Stack>

      {/* ── Summary ── */}
      {reportData && !loading && (
        <>
          <Typography variant="h6" fontWeight={600} textAlign="center" mt={2}>Totals</Typography>
          <SummaryBar summary={reportData.summary} />
        </>
      )}

      {/* ── Status ── */}
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress color="primary" />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
      {reportData && !loading && reportData.groups?.length === 0 && (
        <Alert severity="info" sx={{ my: 2 }}>No data for the selected filters.</Alert>
      )}

      {/* ── Report groups ── */}
      {reportData && !loading && (() => {
        const renderSection = (sprayMode) => {
          const sectionGroups = reportData.groups
            ?.map(group => ({
              ...group,
              rows: group.rows.filter(r => r.sprayMode === sprayMode)
            }))
            .filter(g => g.rows.length > 0);

          if (!sectionGroups || sectionGroups.length === 0) return null;

          return (
            <Box key={sprayMode} mb={4}>
              <Typography variant="h6" fontWeight={700} mb={2}
                sx={{ borderBottom: '2px solid', borderColor: 'divider', pb: 0.5 }}>
                {sprayMode}
              </Typography>
              {sectionGroups.map((group, gi) => (
                <Box key={gi} mb={3}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1}>
                    {groupBy === 'Farm'      && `Farm: ${group.groupLabel}`}
                    {groupBy === 'Field'     && `Block: ${group.groupLabel}`}
                    {groupBy === 'FarmEvent' && `Farm Event: ${group.groupLabel}`}
                    {groupBy === 'Sprayer'   && `Sprayer: ${group.groupLabel}`}
                  </Typography>
                  <ReportTable rows={group.rows} totals={group} isBroadcast={sprayMode === 'Broadcast'} />
                </Box>
              ))}
            </Box>
          );
        };

        return (
          <>
            {selectedSprayModes.includes('Precision')  && renderSection('Precision')}
            {selectedSprayModes.includes('Broadcast')  && renderSection('Broadcast')}
          </>
        );
      })()}

      {/* ── Export buttons ── */}
      {hasResults && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={exportCsv}
            >
              Export CSV
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}

